/* eslint-disable camelcase */
import { Box, FormControl, FormLabel, HStack, Switch, VStack } from '@chakra-ui/react';
import { ColumnFiltersState, createColumnHelper, Row } from '@tanstack/react-table';
import React from 'react';
import Select from 'react-select';
import { useImmerReducer } from 'use-immer';

import AvailablePreviewList from '@/components/available-preview-list/AvailablePreviewList';
import CustomOrderAlert from '@/components/custom-order-alert/CustomOrderAlert';
import FilterSelect, { FilterSelectValue } from '@/components/filter-select/FilterSelect';
import {
  BASIC_TABLE_SORT,
  DEFAULT_COLOR_ITEM,
  DEFAULT_Z_INDEX,
  NUMERICAL_MINUS_ONE,
  NUMERICAL_ONE,
  NUMERICAL_ZERO,
} from '@/constants/defaults';
import { FilterSelectType, ViewEditorAssignmentColumns, ViewEditorColumnFilterFields } from '@/constants/enums';
import { VIEW_EDITOR_SELECT_WIDTH } from '@/constants/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  makeSelectCustomSortSelectedForTab,
  setViewEditorDraftAssignments,
  setViewEditorDraftAutoAddAssignments,
  setViewEditorDraftSortByAssignment,
  setViewEditorHideInactiveAssignments,
} from '@/store/slices/viewEditor.slice';
import { RootState } from '@/store/store';
import { AssignmentStructure } from '@/types/assignment.types';
import { AssignmentSortOrderDropDownOption, AssignmentSortOrderDropDownOptions } from '@/types/ui.types';
import { ViewColoredItem, ViewEditorDraftSortByAssignmentOptions, ViewLayoutListColumns } from '@/types/view.types';
import { tableColumnFilterFn } from '@/utils/filterFns';
import { updateColumnFilter } from '@/utils/updaters';

// Key to sort items on the tables
const ASSIGNMENT_SORT_KEY = 'id';

interface ViewEditorAssignmentsProps {
  assignmentsData: AssignmentStructure[];
  isAssignmentDataLoading: boolean;
}

type AssignmentColored = AssignmentStructure & ViewColoredItem;

const timeThenName = (rowA: Row<AssignmentColored>, rowB: Row<AssignmentColored>) => {
  const aTime = rowA.original.startDate;
  const aName = rowA.original.displayName;
  const bTime = rowB.original.startDate;
  const bName = rowB.original.displayName;

  if (aTime === bTime) {
    if (aName === bName) return NUMERICAL_ZERO;
    return aName < bName ? NUMERICAL_MINUS_ONE : NUMERICAL_ONE;
  }

  return aTime < bTime ? NUMERICAL_MINUS_ONE : NUMERICAL_ONE;
};

interface ViewEditorAssignmentsState {
  assignmentSortValue: AssignmentSortOrderDropDownOption;
  assignmentToggleFilters: {
    autoAddAssignments: boolean | undefined;
  };
  availableItemsColumnFilters: ColumnFiltersState;
  selectedAssignmentTypes: number[];
}

enum ReducerActionTypes {
  SET_ASSIGNMENT_SORT_VALUE = 'SET_ASSIGNMENT_SORT_VALUE',
  SET_AVAILABLE_ITEMS_COLUMN_FILTERS = 'SET_AVAILABLE_ITEMS_COLUMN_FILTERS',
  SET_SELECTED_ASSIGNMENT_TYPES = 'SET_SELECTED_ASSIGNMENT_TYPES',
  TOGGLE_AUTO_ADD_ASSIGNMENTS = 'TOGGLE_AUTO_ADD_ASSIGNMENTS',
}

type ReducerAction = { type: ReducerActionTypes; payload: unknown };

const stateReducer = (draft: ViewEditorAssignmentsState, action: ReducerAction) => {
  switch (action.type) {
    case ReducerActionTypes.SET_ASSIGNMENT_SORT_VALUE: {
      draft.assignmentSortValue = action.payload as AssignmentSortOrderDropDownOption;
      return;
    }
    case ReducerActionTypes.TOGGLE_AUTO_ADD_ASSIGNMENTS: {
      draft.assignmentToggleFilters.autoAddAssignments = !draft.assignmentToggleFilters.autoAddAssignments;
      return;
    }
    case ReducerActionTypes.SET_SELECTED_ASSIGNMENT_TYPES: {
      draft.selectedAssignmentTypes = action.payload as number[];
      return;
    }
    case ReducerActionTypes.SET_AVAILABLE_ITEMS_COLUMN_FILTERS: {
      draft.availableItemsColumnFilters = action.payload as ColumnFiltersState;
      return;
    }
  }
};

const ViewEditorAssignments = (props: ViewEditorAssignmentsProps) => {
  const { assignmentsData, isAssignmentDataLoading } = props;

  const {
    uiState: { assignmentTypes, hideInactiveAssignments, isListViewMultiSort },
    viewDraft: view,
  } = useAppSelector((state: RootState) => state.viewEditor);

  const dispatch = useAppDispatch();

  const { filter } = view;

  const customSortSelectedAssignments = React.useMemo(() => makeSelectCustomSortSelectedForTab(), []);

  const selectIsAssignmentsCustomSort = useAppSelector((state: RootState) =>
    customSortSelectedAssignments(state, ViewLayoutListColumns.ASSIGNMENT),
  );

  const [state, localDispatch] = useImmerReducer(stateReducer, {
    assignmentSortValue:
      AssignmentSortOrderDropDownOptions.find(({ value }) => value === filter.sort_assignments_by) ??
      // eslint-disable-next-line no-magic-numbers
      AssignmentSortOrderDropDownOptions[0],
    assignmentToggleFilters: {
      autoAddAssignments: filter.auto_add_assignments,
    },
    availableItemsColumnFilters: [] as ColumnFiltersState,
    selectedAssignmentTypes: [],
  });

  const handleItemsChanged = React.useCallback(
    (items: AssignmentColored[]) => {
      const coloredItems = items.map((item) => {
        return {
          assignmentStructure: {
            isMapped: item.isMapped,
            isParent: item.isParent,
            templateId: item.templateId,
          },
          color: item.color,
          colorText: item.colorText,
          id: item.id,
        };
      });

      if (coloredItems.length === filter.on_assignments.length) {
        const itemsAreTheSame = coloredItems.every((item, idx) => {
          const filterAssignment = filter.on_assignments[idx];

          return (
            item.id === filterAssignment.id &&
            item.color === filterAssignment.color &&
            item.colorText === filterAssignment.colorText
          );
        });

        if (itemsAreTheSame) return;
      }

      dispatch(setViewEditorDraftAssignments(coloredItems));
    },
    [dispatch, filter.on_assignments],
  );

  const currentAssignmentStructureIds = React.useMemo(() => {
    return filter.on_assignments.map((assignment) => assignment.id);
  }, [filter.on_assignments]);

  const [availableItems, previewItems] = React.useMemo(() => {
    const availableItems = new Map<number, AssignmentColored>();
    const previewItems = new Map<number, AssignmentColored>();

    // If data are loading or there are no data, return empty maps
    if (isAssignmentDataLoading || !assignmentsData) {
      return [availableItems, previewItems];
    }

    // Hydrate available items
    for (const assignment of assignmentsData) {
      const { id } = assignment;

      if (currentAssignmentStructureIds.includes(id)) {
        continue;
      }

      const { color, colorText } = DEFAULT_COLOR_ITEM;
      const coloredAssignment = { ...assignment, color, colorText, id: assignment.id };

      availableItems.set(id, coloredAssignment);
    }

    // Hydrate preview items
    for (const filterAssignment of filter.on_assignments) {
      const { color, colorText, id } = filterAssignment;
      const assignment = assignmentsData.find((data) => data.id === id);

      if (!assignment) continue;

      const coloredAssignment = { ...assignment, color, colorText, id: assignment.id };

      previewItems.set(id as number, coloredAssignment);
    }

    return [availableItems, previewItems];
  }, [assignmentsData, currentAssignmentStructureIds, filter.on_assignments, isAssignmentDataLoading]);

  React.useEffect(() => {
    if (isAssignmentDataLoading) return;
    handleItemsChanged([...previewItems.values()]);
  }, [handleItemsChanged, isAssignmentDataLoading, previewItems]);

  const assignmentInactiveFilterFn = (row: Row<AssignmentColored>) => {
    return !(row.getValue(ViewEditorAssignmentColumns.expired) as boolean);
  };

  const assignmentTypeFilterFn = (row: Row<AssignmentColored>, _: string, filterValue: number[]) => {
    if (!filterValue.length) return true;

    const assignTypeId = row.getValue(ViewEditorAssignmentColumns.assignTypeId);

    return filterValue.includes(assignTypeId as number);
  };

  assignmentTypeFilterFn.autoRemove = (val: string) => !val;

  const columnHelper = createColumnHelper<AssignmentColored>();

  const availableItemsColumns = [
    // eslint-disable-next-line react/prop-types
    columnHelper.accessor((row) => row.displayName, {
      cell: undefined,
      enableSorting: true,
      filterFn: tableColumnFilterFn,
      header: () => <Box>Name</Box>,
      id: ViewEditorAssignmentColumns.displayName,
      sortingFn: BASIC_TABLE_SORT,
    }),
    // eslint-disable-next-line react/prop-types
    columnHelper.accessor((row) => row.typeId, {
      cell: undefined,
      enableHiding: true,
      filterFn: assignmentTypeFilterFn,
      id: ViewEditorAssignmentColumns.assignTypeId,
    }),
    // eslint-disable-next-line react/prop-types
    columnHelper.accessor((row) => row.expired, {
      cell: undefined,
      enableHiding: true,
      filterFn: assignmentInactiveFilterFn,
      id: ViewEditorAssignmentColumns.expired,
    }),
    // eslint-disable-next-line react/prop-types
    columnHelper.accessor((row) => row.id, {
      cell: undefined,
      enableHiding: true,
      header: undefined,
      id: ViewEditorAssignmentColumns.id,
    }),
  ];

  const previewColumnsHelper = createColumnHelper<AssignmentColored>();

  const previewColumnFilterFn = React.useMemo(() => {
    const { value } = state.assignmentSortValue;

    switch (value as string) {
      case ViewEditorDraftSortByAssignmentOptions.NAME:
        return BASIC_TABLE_SORT;
      case ViewEditorDraftSortByAssignmentOptions.TIME_THEN_NAME:
        return timeThenName;
      default:
        return BASIC_TABLE_SORT;
    }
  }, [state.assignmentSortValue]);

  // if MultiSorting is Active and View has LIST layout, we should disable sorting
  const getPreviewEnableSorting = !isListViewMultiSort;

  const previewColumns = [
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.displayName, {
      cell: undefined,
      enableSorting: getPreviewEnableSorting,
      filterFn: tableColumnFilterFn,
      header: () => <Box>Name</Box>,
      id: ViewEditorAssignmentColumns.displayName,
      sortingFn: previewColumnFilterFn,
    }),
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.typeId, {
      cell: undefined,
      enableHiding: true,
      // filterFn: assignmentTypeFilterFn,
      id: ViewEditorAssignmentColumns.assignTypeId,
    }),
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.expired, {
      cell: undefined,
      enableHiding: true,
      filterFn: 'equals',
      id: ViewEditorAssignmentColumns.expired,
    }),
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.id, {
      cell: undefined,
      enableHiding: true,
      header: undefined,
      id: ViewEditorAssignmentColumns.id,
    }),
  ];

  const handleAutoAddAssignmentsToggle = () => {
    localDispatch({
      payload: null,
      type: ReducerActionTypes.TOGGLE_AUTO_ADD_ASSIGNMENTS,
    });
    dispatch(setViewEditorDraftAutoAddAssignments(!state.assignmentToggleFilters.autoAddAssignments));
  };

  const handleSelectAssignmentType = (values: unknown) => {
    const selectedAssignmentTypes = (values as FilterSelectValue[]).map((assignmentType) => assignmentType.value);

    localDispatch({
      payload: selectedAssignmentTypes,
      type: ReducerActionTypes.SET_SELECTED_ASSIGNMENT_TYPES,
    });

    const updatedAvailableItemsColumnFilters = updateColumnFilter(
      ViewEditorColumnFilterFields.ASSIGN_TYPE_ID,
      selectedAssignmentTypes,
      state.availableItemsColumnFilters,
    );

    localDispatch({
      payload: updatedAvailableItemsColumnFilters,
      type: ReducerActionTypes.SET_AVAILABLE_ITEMS_COLUMN_FILTERS,
    });
  };

  const handleInactiveAssignmentsToggle = React.useCallback(() => {
    dispatch(setViewEditorHideInactiveAssignments(!hideInactiveAssignments));
  }, [dispatch, hideInactiveAssignments]);

  React.useEffect(() => {
    const expiredColumnFilter = (state.availableItemsColumnFilters as ColumnFiltersState).find(
      (filter) => filter.id === ViewEditorAssignmentColumns.expired,
    );

    // Filter is enabled for table, but disabled in ui
    // OR filter is not enabled for table, but enabled in ui
    const isFilterEnabledInUIOrTable =
      (expiredColumnFilter && !hideInactiveAssignments) || (!expiredColumnFilter && hideInactiveAssignments);
    // If Filter is enabled for table or in UI we need to update the state

    if (isFilterEnabledInUIOrTable) {
      const columnFiltersDraft = updateColumnFilter(
        ViewEditorAssignmentColumns.expired,
        hideInactiveAssignments,
        state.availableItemsColumnFilters,
        [ViewEditorAssignmentColumns.expired],
      );

      localDispatch({
        payload: columnFiltersDraft,
        type: ReducerActionTypes.SET_AVAILABLE_ITEMS_COLUMN_FILTERS,
      });
    }
  }, [hideInactiveAssignments, localDispatch, state]);

  const getTopBar = () => {
    return (
      <HStack spacing="24px" alignItems="flex-start">
        <VStack alignItems="flex-start">
          <FormLabel>Filter Available Assignments by Assignment Types</FormLabel>
          <FilterSelect
            noGap
            hasSelectAll={true}
            inputName={FilterSelectType.FILTER_BY_ASSIGNMENT_TYPE}
            isMultiSelect={true}
            onChangeHandler={handleSelectAssignmentType}
            optionLabelKey={'name'}
            options={assignmentTypes}
            optionValueKey={'atypeId'}
            placeHolderText={'Assignment Types'}
            selectedValues={state.selectedAssignmentTypes}
            styles={{ container: (styles) => ({ ...styles, zIndex: DEFAULT_Z_INDEX }) }}
          />
        </VStack>

        {/* hide Sort By field for LIST layout if Multi Sort Flag is ON */}
        {!isListViewMultiSort && (
          <VStack alignItems="flex-start">
            <FormLabel>Sort Selected Assignments by</FormLabel>
            <Select
              options={AssignmentSortOrderDropDownOptions}
              onChange={(option) => {
                localDispatch({ payload: option, type: ReducerActionTypes.SET_ASSIGNMENT_SORT_VALUE });
                dispatch(
                  setViewEditorDraftSortByAssignment(option?.value ?? ViewEditorDraftSortByAssignmentOptions.CUSTOM),
                );
              }}
              styles={{
                container: (styles) => ({
                  ...styles,
                  zIndex: DEFAULT_Z_INDEX,
                }),
                control: (styles) => ({
                  ...styles,
                  maxWidth: VIEW_EDITOR_SELECT_WIDTH,
                  minWidth: VIEW_EDITOR_SELECT_WIDTH,
                }),
              }}
              value={state.assignmentSortValue}
            />
          </VStack>
        )}

        <VStack alignItems="flex-end" height="100%" justifyContent="flex-end">
          <FormControl display="flex" alignItems="center">
            <HStack w={'240px'} justifyContent={'space-between'}>
              <FormLabel htmlFor="auto-add-assignments" mb="0">
                Auto-Add Assignments
              </FormLabel>
              <Switch
                id="auto-add-assignments"
                isChecked={state.assignmentToggleFilters.autoAddAssignments}
                onChange={handleAutoAddAssignmentsToggle}
              />
            </HStack>
          </FormControl>
          <FormControl display="flex" alignItems="center">
            <HStack w={'240px'} justifyContent={'space-between'}>
              <FormLabel htmlFor="hide-inactive-assignments-from-available" mb="0">
                Hide Inactive Assignments
              </FormLabel>
              <Switch
                id="hide-inactive-assignments-from-available"
                isChecked={hideInactiveAssignments}
                onChange={handleInactiveAssignmentsToggle}
              />
            </HStack>
          </FormControl>
        </VStack>
      </HStack>
    );
  };

  return (
    <VStack gap={5} align={'top'}>
      {getTopBar()}
      {selectIsAssignmentsCustomSort && <CustomOrderAlert />}
      <AvailablePreviewList
        availableColumns={availableItemsColumns}
        availableColumnsVisibility={{
          [ViewEditorAssignmentColumns.id]: false,
          [ViewEditorAssignmentColumns.assignTypeId]: false,
          [ViewEditorAssignmentColumns.expired]: false,
        }}
        availableItems={availableItems}
        availableItemsColumnFilters={state.availableItemsColumnFilters}
        availableItemsPrimaryColumnId={ViewEditorAssignmentColumns.displayName}
        availableItemsTableLabel={'Available Assignments'}
        availableRowUniqueKey={ASSIGNMENT_SORT_KEY}
        isOpen={true}
        isLoading={isAssignmentDataLoading}
        onItemsChanged={handleItemsChanged}
        overrideSorting={
          selectIsAssignmentsCustomSort ||
          [
            ViewEditorDraftSortByAssignmentOptions.CUSTOM,
            ViewEditorDraftSortByAssignmentOptions.TIME_THEN_CUSTOM,
          ].includes(filter.sort_assignments_by)
        }
        previewColumns={previewColumns}
        previewColumnsVisibility={{
          [ViewEditorAssignmentColumns.id]: false,
          [ViewEditorAssignmentColumns.assignTypeId]: false,
          [ViewEditorAssignmentColumns.expired]: false,
        }}
        previewItems={previewItems}
        previewItemsPrimaryColumnId={ViewEditorAssignmentColumns.displayName}
        previewItemsTableLabel={'Selected Assignments'}
        previewRowUniqueKey={ASSIGNMENT_SORT_KEY}
        primaryAvailableColumnIndex={0}
        primaryPreviewColumnIndex={0}
        showColorPicker={true}
        showSideControls={true}
        tableColumnIds={ViewEditorAssignmentColumns}
      />
    </VStack>
  );
};

export default ViewEditorAssignments;
